<template>
  <div class="showroom-social">
    <p class="text-grey text-sm">
      {{ $t('SHOWROOM_ADMIN.SOCIALS.SUBHEADING') }}
    </p>
    <div class="selector">
      <button
        v-for="userLinkSetting in userLinks"
        :key="userLinkSetting.key"
        class="btn btn-ghost"
        :class="{
          active: userLinkSetting.active,
          'custom-button': userLinkSetting.key === UserLinkType.CUSTOM,
        }"
        @click="toggleChannel(userLinkSetting.key)"
      >
        <Icon :icon="userLinkSetting.icon" />
        <p>{{ $t(userLinkSetting.name) }}</p>
      </button>
    </div>
    <div class="details">
      <div
        v-for="(userLink, index) in activeUserLinks"
        :key="userLink.type"
        :class="{
          dragging: draggedIndex === index,
        }"
        :draggable="dragEnabled === index"
        @dragstart="handleDragStart(index, $event)"
        @dragend="handleDragEnd"
        @dragover.prevent="handleDragOver(index)"
        @drop.prevent="handleDrop(index)"
      >
        <Icon
          icon="reorder"
          class="drag-icon"
          @mousedown="enableDragForRow($event, index)"
          @mouseup="disableDragForRow(index)"
          @mouseleave="disableDragForRow(index)"
          @touchstart="enableDragForRow($event, index)"
          @touchend="disableDragForRow(index)"
          @touchcancel="disableDragForRow(index)"
        />
        <Icon :icon="userLink.setting.icon" class="social-icon" />
        <p>{{ $t(userLink.setting.name) }}</p>
        <LabelInput
          class="lable-input"
          :class="{ 'has-error': userLink.hasError }"
        >
          <label for="username">
            {{ $t('SHOWROOM_ADMIN.SOCIALS.USERNAME') }}
          </label>
          <DebounceInput
            id="username"
            type="text"
            :model-value="userLink.value"
            :maxlength="100"
            @update:model-value="
              (value: string) =>
                marketplaceStore.setUserLinkValue(userLink.type, value)
            "
          />
          <div v-if="userLink.hasError" class="field-info">
            <small class="error-info error-info-name">
              {{ $t('SHOWROOM_ADMIN.SOCIALS.USERNAME_ERROR') }}
            </small>
          </div>
        </LabelInput>

        <a
          class="icon-link"
          @click="() => marketplaceStore.removeUserLink(userLink.type)"
        >
          <Icon icon="x" />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  USER_LINK_SETTINGS,
  UserLinkType,
  UserLink,
} from 'commons/api/showroomService/showroomService';
import LabelInput from 'commons/labelInput/LabelInput.vue';
import { useMarketplaceStore } from 'commons/store/marketplace';
import DebounceInput from 'src/app/components/input/DebounceInput.vue';
import { computed, ref } from 'vue';

const marketplaceStore = useMarketplaceStore();

const draggedIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);
const tempUserLinks = ref<UserLink[] | null>(null);
const dragEnabled = ref<number | null>(null);

const activeUserLinks = computed(() => {
  // use temp list during reordering
  const links = tempUserLinks.value || marketplaceStore.showroomData?.userLinks;
  return links?.map((userLink) => ({
    ...userLink,
    setting: USER_LINK_SETTINGS[userLink.type],
    hasError: !!marketplaceStore.validation.userLinkErrors[userLink.type],
  }));
});
const userLinks = computed(() =>
  Object.values(USER_LINK_SETTINGS).map((userLinkSettings) => ({
    ...userLinkSettings,
    active: !!activeUserLinks.value?.find(
      (link) => link.type === userLinkSettings.key,
    ),
  })),
);

const toggleChannel = (key: UserLinkType) => {
  const isActive = !!activeUserLinks.value?.find((link) => link.type === key);
  if (isActive) {
    marketplaceStore.removeUserLink(key);
  } else {
    marketplaceStore.setUserLinkValue(key, '');
  }
};

// drag & drop handling
const enableDragForRow = (_event: MouseEvent | TouchEvent, index: number) => {
  dragEnabled.value = index;
};

const disableDragForRow = (index: number) => {
  if (draggedIndex.value === null && dragEnabled.value === index) {
    dragEnabled.value = null;
  }
};

const handleDragStart = (index: number, event: DragEvent) => {
  draggedIndex.value = index;
  tempUserLinks.value = [...(marketplaceStore.showroomData?.userLinks || [])];
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/html', index.toString());
  }
};

const handleDragEnd = () => {
  draggedIndex.value = null;
  dragOverIndex.value = null;
  dragEnabled.value = null;
  if (tempUserLinks.value) {
    marketplaceStore.showroomData!.userLinks = tempUserLinks.value;
  }
  tempUserLinks.value = null;
};

const handleDragOver = (index: number) => {
  if (
    draggedIndex.value !== null &&
    draggedIndex.value !== index &&
    tempUserLinks.value
  ) {
    const newArray = [...tempUserLinks.value];
    const draggedItem = newArray[draggedIndex.value];

    newArray.splice(draggedIndex.value, 1);
    newArray.splice(index, 0, draggedItem);

    tempUserLinks.value = newArray;
    draggedIndex.value = index;
  }
};

const handleDrop = (_dropIndex: number) => {
  draggedIndex.value = null;
  dragOverIndex.value = null;
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

.selector {
  display: grid;
  grid-template-columns: repeat(auto-fit, 95px);
  gap: 8px;

  & > button {
    padding: 8px 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    justify-content: center;

    .icon {
      height: 24px;
      width: 24px;
      margin: 4px;
      flex-shrink: 0;
    }

    p {
      margin: 0;
      text-align: center;
      line-height: 1.2;
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &.custom-button {
      grid-column: span 2;
      justify-self: start;
      width: max-content;

      p {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: unset;
      }
    }

    &:not(.active):not(:hover) {
      border-width: 1px;
      border-color: $grey40;
      margin: 1px;

      .icon {
        color: $grey90;
      }

      p {
        color: $grey90;
        font-weight: normal;
      }
    }
  }
}

.details {
  margin-top: 16px;

  & > div {
    display: flex;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid $grey10;
    transition: all 0.2s ease;
    cursor: default;
    background-color: $white;

    &:first-child {
      border-top: 1px solid $grey10;
    }

    &.dragging {
      opacity: 0.9;
      transform: scale(0.98);
      background-color: rgba($pa-color-main, 0.05);
    }

    p {
      margin: 0;
    }

    .icon {
      padding: 4px;
      height: 32px;
      width: 32px;
      margin-right: 8px;
    }

    .drag-icon {
      color: $grey60;
      cursor: grab;

      &:hover {
        color: $grey90;
      }

      &:active {
        cursor: grabbing;
      }
    }

    &:active {
      cursor: grabbing;
    }
  }

  .lable-input {
    width: 288px;
    margin-left: auto;
  }

  :deep(.label-input-wrapper) {
    transition: margin-top 0.2s linear;

    &.active,
    &.active label {
      margin-top: 10px;
    }
  }

  .icon-link {
    margin-left: 8px;
    .icon {
      width: 32px;
      height: 32px;
      margin: 0;
    }
  }
}
</style>
